import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, FileSpreadsheet, BellRing, Settings2, Check, Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, KpiCard } from "@/components/KpiCard";
import { TollDialog } from "@/components/tolls/TollDialog";
import { TollAdminDialog } from "@/components/tolls/TollAdminDialog";
import { download } from "@/components/phones/PhoneRegister";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { useSort, SortTh } from "@/lib/sort";

const FILTERS = [["all", "Tutti"], ["unpaid", "Da pagare"], ["paid", "Pagati"], ["alert", "Con avviso"]];
const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fd = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");
const rowCls = (i) => (i.attention ? "bg-rose-50 hover:bg-rose-100/70" : i.alert || i.admin_note ? "bg-amber-50 hover:bg-amber-100/70" : "hover:bg-slate-50");

export default function TollsPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [stations, setStations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [stationId, setStationId] = useState("all");
  const [data, setData] = useState({ items: [], summary: {} });
  const [editing, setEditing] = useState(null);
  const [managing, setManaging] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const sid = isAdmin && stationId !== "all" ? stationId : undefined;
  const load = useCallback(() => { api.get("/tolls", { params: { station_id: sid } }).then((r) => setData(r.data)); }, [sid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/stations").then((r) => setStations(r.data));
    api.get("/fleet/vehicles").then((r) => setVehicles(r.data.vehicles));
  }, []);

  const items = useMemo(() => {
    const t = q.trim().toLowerCase();
    return data.items.filter((i) => (filter === "all" || (filter === "paid" ? i.paid : filter === "unpaid" ? !i.paid : i.attention || i.admin_note))
      && (!t || [i.plate, i.notice_number, i.company, i.driver_name, i.entry_station, i.exit_station, i.station_name].some((x) => (x || "").toLowerCase().includes(t))));
  }, [data.items, q, filter]);
  const { sorted, sort, toggle } = useSort(items);
  const S = (p) => <SortTh sort={sort} onSort={toggle} {...p} />;
  const seen = async (i) => { try { await api.post(`/tolls/${i.id}/seen`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (i) => {
    if (!window.confirm(`Eliminare il mancato pedaggio ${i.notice_number || i.plate}?`)) return;
    try { await api.delete(`/tolls/${i.id}`); toast.success("Eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const s = data.summary;

  return (
    <div className="fade-up">
      <PageHeader title="Mancati pedaggi" subtitle="Mancati pagamenti autostradali, foto e gestione del pagamento.">
        <Button variant="outline" onClick={() => download("/tolls/export", { station_id: sid }, "mancati_pedaggi.xlsx")} data-testid="toll-export-button"><FileSpreadsheet className="mr-2 h-4 w-4" /> Scarica Excel</Button>
        <Button onClick={() => setEditing({ station_id: sid || "" })} data-testid="toll-add-button"><Plus className="mr-2 h-4 w-4" /> Nuovo mancato pedaggio</Button>
      </PageHeader>
      {isAdmin && (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="toll-station-tabs">
          {[{ id: "all", name: "Tutte le stazioni" }, ...stations].map((st) => (
            <button key={st.id} onClick={() => setStationId(st.id)} data-testid={`toll-tab-${st.id}`}
              className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${stationId === st.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>{st.name}</button>
          ))}
        </div>
      )}
      {!isAdmin && s.alerts > 0 && (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm font-semibold text-amber-900" data-testid="toll-alert-banner">
          <BellRing className="h-5 w-5 shrink-0 animate-pulse" /> L'amministrazione ha lasciato {s.alerts} {s.alerts === 1 ? "avviso" : "avvisi"} da leggere: righe colorate qui sotto.
        </div>
      )}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <KpiCard label="Mancati pedaggi" value={s.total ?? 0} accent="#0F172A" testId="toll-kpi-total" />
        <KpiCard label="Da pagare" value={s.unpaid ?? 0} accent="#DC2626" testId="toll-kpi-unpaid" />
        <KpiCard label="Importo da pagare" value={eur(s.unpaid_amount)} accent="#D97706" testId="toll-kpi-amount" />
        <KpiCard label="Pagati" value={s.paid ?? 0} accent="#059669" testId="toll-kpi-paid" />
        <KpiCard label="Totale pagato" value={eur(s.paid_amount)} accent="#047857" testId="toll-kpi-paid-amount" />
        <KpiCard label="Da attenzionare" value={s.attention ?? 0} accent="#BE123C" testId="toll-kpi-attention" />
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex flex-wrap rounded-lg border border-slate-200 bg-white p-1">
          {FILTERS.map(([k, l]) => <button key={k} onClick={() => setFilter(k)} data-testid={`toll-filter-${k}`} className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${filter === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>)}
        </div>
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca targa, numero, driver, casello..." className="pl-9" data-testid="toll-search-input" />
        </div>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="toll-table">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 [&_th]:whitespace-nowrap">
              <tr><S k="toll_date">Data</S><S k="plate">Targa</S><S k="notice_number">N. mancato pag.</S><S k="company">Società</S><S k="entry_station">Entrata</S><S k="exit_station">Uscita</S>
                <S k="driver_name">Driver</S><S k="amount" align="right">Importo</S><S k="paid">Pagamento</S><th className="px-3 py-2">Nota amministrazione</th><th className="px-3 py-2" /></tr>
            </thead>
            <tbody className="divide-y">
              {sorted.map((i) => (
                <tr key={i.id} className={rowCls(i)} data-testid={`toll-row-${i.id}`}>
                  <td className="whitespace-nowrap px-3 py-2">{fd(i.toll_date)}</td>
                  <td className="whitespace-nowrap px-3 py-2"><span className="font-mono-num rounded-md border border-slate-300 bg-white px-2 py-0.5 text-xs font-semibold">{i.plate}</span>{isAdmin && <span className="ml-2 text-xs text-slate-500">{i.station_name}</span>}</td>
                  <td className="font-mono-num whitespace-nowrap px-3 py-2 text-xs">{i.notice_number || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs" data-testid={`toll-company-${i.id}`}>{i.company || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs">{i.entry_station || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs">{i.exit_station || "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2">{i.driver_name || "—"}</td>
                  <td className="font-mono-num whitespace-nowrap px-3 py-2 text-right font-semibold">{eur(i.amount)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs font-semibold" data-testid={`toll-paid-${i.id}`}>{i.paid ? <span className="text-emerald-700">Pagato il {fd(i.paid_date)}</span> : <span className="text-rose-700">Da pagare</span>}</td>
                  <td className="min-w-[12rem] px-3 py-2 text-xs" data-testid={`toll-note-${i.id}`}>
                    {i.attention && <span className="mr-1 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white">Da attenzionare</span>}{i.admin_note}
                    {!isAdmin && i.alert && <Button size="sm" variant="outline" className="ml-2 h-6 px-2 text-[11px]" onClick={() => seen(i)} data-testid={`toll-seen-${i.id}`}><Check className="mr-1 h-3 w-3" /> Letto</Button>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    {i.photos.length > 0 && <span className="mr-1 inline-flex items-center text-xs text-slate-500"><Image className="mr-0.5 h-3.5 w-3.5" />{i.photos.length}</span>}
                    {isAdmin && <Button size="sm" variant="ghost" title="Gestione pagamento e note" onClick={() => setManaging(i)} data-testid={`toll-manage-edit-${i.id}`}><Settings2 className="h-4 w-4" /></Button>}
                    {(isAdmin || !i.paid) && <Button size="sm" variant="ghost" onClick={() => setEditing(i)} data-testid={`toll-edit-${i.id}`}><Pencil className="h-4 w-4" /></Button>}
                    {isAdmin && <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => remove(i)} data-testid={`toll-delete-${i.id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-500" data-testid="toll-empty">Nessun mancato pedaggio.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <TollDialog item={editing} stations={stations} vehicles={vehicles} isAdmin={isAdmin} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      {managing && <TollAdminDialog item={managing} onClose={() => setManaging(null)} onSaved={() => { setManaging(null); load(); }} />}
    </div>
  );
}
